<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { IconSize } from '~/utils/icon'

const props = withDefaults(defineProps<{
  icon: IconDefinition
  badge?: BadgeValue
  size?: IconSize
}>(), {
  size: 'md',
})

const badgeRef = useTemplateRef<HTMLDivElement>('badge')

// Make the badge pop when the value changes
watch(() => props.badge, (newValue, oldValue) => {
  if (oldValue === newValue || !badgeRef.value) {
    return
  }

  badgeRef.value.classList.remove('animate-pop')
  // This looks strange, but we need to trigger a reflow for the animation to work more than once. Calling offsetWidth on an element will do that.
  void badgeRef.value.offsetWidth
  badgeRef.value.classList.add('animate-pop')
})

const { theme } = useTheme()

const factor = computed(() => {
  return ICON_SIZE_REM[props.size] / ICON_SIZE_REM.md
})

const translateX = computed(() => {
  const badgeFactor = props.badge === 'alert' ? 6 : props.badge === 'notification' ? 5 : 2.5
  return props.badge ? `${-0.3 * badgeFactor * factor.value}rem` : '0'
})

const top = computed(() => `${0.25 * factor.value}rem`)
</script>

<template>
  <ClientOnly>
    <Icon :icon="icon" class="transition-all" :size="size" />
    <div
      ref="badge"
      aria-hidden="true"
      :class="iconBadgeVariants({ theme, visible: !!badge, alert: badge === 'alert', notification: badge === 'notification' })"
    >
      {{ typeof badge === 'number' ? badge : '' }}
    </div>
  </ClientOnly>
</template>

<style scoped>
svg {
  transform: translateX(v-bind(translateX));
}

div {
  transform: scale(v-bind(factor));
  top: v-bind(top);
}
</style>
